import { useState, useEffect, useRef, useMemo } from "react";
import useAxios from "../utils/useAxios"; // Import the custom Axios instance
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { formatIndianCurrency } from "../utils/currencyFormat";
import { hideModal, showModal } from "../utils/modalUtility";
import { currentDateAndTime } from "../utils/generateCurrentDateAndTime";
import { useAgencies } from "../hooks/useAgencies";

const CURRENT_YEAR = new Date().getFullYear();

const ALL_LEVELS = ["BRONZE", "SILVER", "GOLD", "DIAMOND"];

const MONTHS = [
  { name: "January", value: "01" },
  { name: "February", value: "02" },
  { name: "March", value: "03" },
  { name: "April", value: "04" },
  { name: "May", value: "05" },
  { name: "June", value: "06" },
  { name: "July", value: "07" },
  { name: "August", value: "08" },
  { name: "September", value: "09" },
  { name: "October", value: "10" },
  { name: "November", value: "11" },
  { name: "December", value: "12" },
];

const EMPTY_DATE_FILTERS = {
  from_date: "",
  to_date: "",
};

const EMPTY_FOLLOW_UP_DATA = {
  year: "",
  from_date: "",
  to_date: "",
  processing_agency: "",
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);
  const [reportData, setReportData] = useState(null);
  const axiosAPI = useAxios(); // Use the custom Axios instance
  const [selectedMonth, setSelectedMonth] = useState("01");
  const [downloading, setDownloading] = useState(false);
  const [bookedCalendarLoading, setBookedCalendarLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadRows, setUploadRows] = useState([]);

  const [want80gLoading, setWant80gLoading] = useState(false);

  const [autoRecurringData, setAutoRecurringData] = useState({
    processing_agency: "",
    csv_file: null,
  });

  const { agencies, loading: agenciesLoading } = useAgencies();

  // console.log("Agencies", agencies);

  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [want80gSelectedYear, setWant80gSelectedYear] = useState(CURRENT_YEAR);

  const currentMonthValue = String(new Date().getMonth() + 1).padStart(2, "0");
  const [donationMonth, setDonationMonth] = useState(currentMonthValue);
  const [donationYear, setDonationYear] = useState(CURRENT_YEAR);
  const [tempMonth, setTempMonth] = useState(currentMonthValue);
  const [tempYear, setTempYear] = useState(CURRENT_YEAR);
  const [statsLoading, setStatsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const receiptRef = useRef();
  const autoRecurringFileInputRef = useRef();

  const [filters, setFilters] = useState(EMPTY_DATE_FILTERS);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_DATE_FILTERS);
  };

  const handleApplyDonationFilter = () => {
    setDonationMonth(tempMonth);
    setDonationYear(tempYear);
  };

  const handleResetDonationFilter = () => {
    setTempMonth(currentMonthValue);
    setTempYear(CURRENT_YEAR);
    setDonationMonth(currentMonthValue);
    setDonationYear(CURRENT_YEAR);
  };

  async function fetchGiftReport() {
    setDownloading(true); //  Start loader
    try {
      const response = await axiosAPI.get(
        `/api/dashboard/download-birthday-wishes-report?&from_date=${filters.from_date}&to_date=${filters.to_date}`, //  Pass table name in query
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `donors.xlsx`); // Optional: dynamic file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      // alert("Failed to download file. Please try again later.");
      alert("No Data Found");
    } finally {
      setDownloading(false); // ⏹ End loader
    }
  }

  const fetchDashboardData = async (
    month = donationMonth,
    year = donationYear,
  ) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setStatsLoading(true);
    }
    try {
      const response = await axiosAPI.get(
        `/api/dashboard/dashboard-data?month=${month}&year=${year}`,
      );
      setDashboardData(response.data); // Set the fetched data to state
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false); // Set loading state to false after data is fetched
      setStatsLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleAutoRecurringSubmit = async (e) => {
    e.preventDefault();

    const { processing_agency, csv_file } = autoRecurringData;

    if (!processing_agency || !csv_file) {
      alert("Please select agency and upload CSV");
      return;
    }

    const formData = new FormData();
    formData.append("processing_agency", processing_agency);
    formData.append("excel", csv_file);
    formData.append("upload_date_and_time", currentDateAndTime());

    try {
      setIsUploading(true);
      const res = await axiosAPI.post(
        "/api/dashboard/upload-auto-recurring-donation",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      // FIXED HERE
      const {
        message = "",
        insertedCount = 0,
        rows = [],
        failedExcelBase64,
      } = res.data.result || {};

      //  VERY IMPORTANT
      setUploadRows(rows);

      // Download failed excel
      if (failedExcelBase64) {
        const bytes = Uint8Array.from(atob(failedExcelBase64), (c) =>
          c.charCodeAt(0),
        );
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "auto_recurring_failed_rows.xlsx";
        link.click();
      }

      const failedRows = rows.filter((r) => r.status === "fail");

      if (rows.length === 0) {
        window.notifyMessage("Error", "No valid data found", "error");
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `Failed Rows: ${failedRows.length}`,
          "error",
        );
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `Inserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning",
        );
      } else {
        window.notifyMessage("Success", "Upload successful", "success");
      }

      autoRecurringFileInputRef.current.value = "";
      setAutoRecurringData({
        processing_agency: "",
        csv_file: null,
      });
    } catch (error) {
      console.error(error);
      window.notifyMessage("Error", "Upload failed", "error");
    } finally {
      setIsUploading(false);
    }
  };

  // console.log("Upload Rows:", uploadRows);

  // console.log("Dashboard Data",dashboardData)

  function getMonthName(value) {
    return MONTHS.find((m) => m.value === value)?.name || "";
  }

  // Ensure you are using empty array as fallback
  const {
    total_donors = 0,
    total_leads = 0,
    total_gifts = 0,
    total_razorpay_donation = 0,
    total_worldline_donation = 0,
    total_direct_bank_transfer_donation = 0,
  } = dashboardData || {};

  const menu = useMemo(
    () => [
      {
        icon: <i className="fas fa-chalkboard-teacher"></i>,
        text: "Total Donors",
        value: total_donors,
        color: "icon-success",
      },
      {
        icon: <i className="fas fa-chalkboard-teacher"></i>,
        text: "Total Leads",
        value: total_leads,
        color: "icon-danger",
        border: "border",
      },
      {
        icon: <i className="fas fa-folder-open"></i>,
        text: "Total Gifts",
        value: total_gifts,
        color: "ua-icon-gray",
      },
      {
        icon: <i className="fas fa-hand-holding-heart"></i>,
        text: "Total Razorpay Donation",
        value: total_razorpay_donation,
        color: "icon-info",
      },
      {
        icon: <i className="fas fa-university"></i>,
        text: "Total Worldline Donation",
        value: total_worldline_donation,
        color: "icon-warning",
      },
      {
        icon: <i className="fas fa-coins"></i>,
        text: "Total Direct Bank Transfer Donation",
        value: total_direct_bank_transfer_donation,
        color: "icon-secondary",
      },
    ],
    [
      total_donors,
      total_leads,
      total_gifts,
      total_razorpay_donation,
      total_worldline_donation,
      total_direct_bank_transfer_donation,
    ],
  );

  const [followUpData, setFollowUpData] = useState(EMPTY_FOLLOW_UP_DATA);

  const downloadFollowUpMandareReportExcel = async (table_name, status) => {
    setDownloading(true); //  Start loader
    // const queryString = buildQuery(filters, "1");

    // console.log("query ", queryString);

    try {
      const response = await axiosAPI.get(
        `/api/donor/download-donor-excel?table=${table_name}&status=${status}&is_lead=yes`, //  Pass table name in query
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}.xlsx`); // Optional: dynamic file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false); //  End loader
    }
  };

  async function downloadWant80gReport() {
    const payload = {
      year: want80gSelectedYear,
    };

    // console.log("Payload", payload.year);

    // return;

    setWant80gLoading(true);
    try {
      const response = await axiosAPI.get(
        "/api/dashboard/download-wantg-report?year=" + payload.year,
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Want_80g_report.xlsx");

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setWant80gLoading(false); // End loader
    }
  }

  const downloadFollowUpExcel = async (table_name, followUpData) => {
    setDownloading(true); // Start loader

    try {
      const params = new URLSearchParams({
        table: table_name,
        ...followUpData,
      }).toString();

      const response = await axiosAPI.get(
        `/api/recurring-donation/download-follow-up-excel?${params}`,
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      hideModal("followUpModal");
      setFollowUpData(EMPTY_FOLLOW_UP_DATA);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false); // End loader
    }
  };

  // Fetch dashboard data when the component mounts or donation filters change
  useEffect(() => {
    fetchDashboardData(donationMonth, donationYear);
  }, [donationMonth, donationYear]);

  const apiData = reportData?.level;

  const levelDataMap = useMemo(() => {
    const mappedData = {};

    // apiData?.forEach((item) => {
    //   mappedData[item.patronship_level] = item.total_count;
    // });

    apiData?.forEach((item) => {
      mappedData[item.patronship_level] = {
        active: Number(item.active_count) || 0,
        onHold: Number(item.on_hold_count) || 0,
        closed: Number(item.closed_count) || 0,
      };
    });

    return mappedData;
  }, [apiData]);

  // Show Bootstrap spinner while loading
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-inner">
        <div className="row">
          {/* General Stats Cards (Row 1) */}
          {/* {menu.slice(0, 3).map((item, index) => {
            return (
              <div className="col-sm-6 col-md-4 mb-3" key={index}>
                <div className="card card-stats card-round">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-icon">
                        <div
                          className={`icon-big text-center ${item.color} ${item.border} bubble-shadow-small`}
                        >
                          {item.icon}
                        </div>
                      </div>
                      <div className="col col-stats ms-3 ms-sm-0">
                        <div className="numbers">
                          <p className="card-category">{item.text}</p>
                          <h4 className="card-title">{item.value}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })} */}

          {/* Donation Channel Title and Filter Row */}
          <div className="col-12 mt-4 mb-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-2">
              <h4 className="fw-bold text-dark mb-0">
                Donation Channel Summary
              </h4>
              {statsLoading && (
                <div
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </div>{" "}
            <div className="d-flex flex-wrap align-items-center gap-3">
              {/* Month Selector */}
              <div className="d-flex align-items-center gap-1">
                <span className="text-muted small fw-semibold me-1">
                  Month:
                </span>
                <select
                  className="form-select border shadow-sm"
                  style={{ width: "130px", height: "38px" }}
                  value={tempMonth}
                  onChange={(e) => setTempMonth(e.target.value)}
                >
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Selector */}
              <div className="d-flex align-items-center gap-1">
                <span className="text-muted small fw-semibold me-1">Year:</span>
                <select
                  className="form-select border shadow-sm"
                  style={{ width: "110px", height: "38px" }}
                  value={tempYear}
                  onChange={(e) => setTempYear(e.target.value)}
                >
                  {Array.from({ length: 6 }, (_, i) => {
                    const year = CURRENT_YEAR - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Filter Button */}
              <button
                className="btn btn-primary rounded-pill px-4"
                style={{ height: "38px" }}
                onClick={handleApplyDonationFilter}
              >
                <i className="fas fa-filter me-2"></i> Filter
              </button>

              {/* Reset Button */}
              <button
                className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "38px", height: "38px" }}
                title="Reset Filters"
                onClick={handleResetDonationFilter}
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>

          {/* Donation Channel Cards (Row 2) */}
          {menu.slice(3, 6).map((item, index) => {
            return (
              <div className="col-sm-6 col-md-4 mb-3" key={index + 3}>
                <div className="card card-stats card-round">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-icon">
                        <div
                          className={`icon-big text-center ${item.color} ${item.border} bubble-shadow-small`}
                        >
                          {item.icon}
                        </div>
                      </div>
                      <div className="col col-stats ms-3 ms-sm-0">
                        <div className="numbers">
                          <p className="card-category">{item.text}</p>
                          <h4 className="card-title">{item.value}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Birthday Report Card */}

          <div className="sm:page-inner">
            <div className="row">
              <div className="col-12 col-md-6 mb-4">
                <div
                  className="card h-100"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading inside the card */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-birthday-cake text-danger fs-4"></i>
                      <h5 className="mb-0 fw-bold text-dark">
                        Birthday Report
                      </h5>
                    </div>

                    {/* Filter Form */}
                    <div className="d-flex flex-wrap align-items-end gap-3">
                      {/* From Date */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="fromDate"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>{" "}
                          From Date
                        </label>
                        <input
                          id="fromDate"
                          type="date"
                          className="form-control shadow-sm border"
                          value={filters.from_date || ""}
                          onChange={(e) =>
                            handleFilterChange("from_date", e.target.value)
                          }
                        />
                      </div>

                      {/* To Date */}
                      <div className="d-flex flex-column">
                        <label
                          htmlFor="toDate"
                          className="form-label fw-semibold mb-1"
                        >
                          <i className="fas fa-calendar-alt me-1 text-primary"></i>{" "}
                          To Date
                        </label>
                        <input
                          id="toDate"
                          type="date"
                          className="form-control shadow-sm border"
                          value={filters.to_date || ""}
                          onChange={(e) =>
                            handleFilterChange("to_date", e.target.value)
                          }
                        />
                      </div>

                      {/* Export Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          onClick={() => {
                            if (filters.from_date && filters.to_date) {
                              fetchGiftReport();
                            } else {
                              alert("Please select both From and To dates.");
                            }
                          }}
                        >
                          <i className="fas fa-file-export me-2"></i> Export
                        </button>
                      </div>

                      {/* Reset Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: "38px", height: "38px" }}
                          title="Reset Filters"
                          onClick={handleResetFilters}
                        >
                          <i className="fas fa-sync-alt"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Export Follow Up Mandate Report Card */}
              <div className="col-12 col-md-6">
                <div
                  className="card mb-4"
                  // style={{ maxWidth: "720px" }}
                >
                  <div className="card-body">
                    {/* Heading with Icon */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="fas fa-chart-line text-success fs-4"></i>

                      <h5 className="mb-0 fw-bold text-dark">
                        Export Want 80g Report
                      </h5>
                    </div>

                    {/* Filters & Actions */}
                    <div className="d-flex flex-wrap align-items-end gap-2">
                      {/* Year Dropdown */}
                      <div>
                        <label className="form-label fw-semibold mb-1">
                          <i className="fas fa-calendar me-1 text-primary"></i>{" "}
                          Selected Year
                        </label>
                        <select
                          className="form-select"
                          style={{ height: "38px" }}
                          value={want80gSelectedYear}
                          onChange={(e) =>
                            setWant80gSelectedYear(e.target.value)
                          }
                        >
                          {[2026, 2025, 2024, 2023, 2022].map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Print Button */}
                      <div>
                        <label className="form-label invisible">.</label>
                        <button
                          className="btn btn-success rounded-pill px-4 w-100"
                          style={{ height: "38px" }}
                          disabled={want80gLoading}
                          onClick={downloadWant80gReport}
                        >
                          <i className="fas fa-print me-2"></i> Export
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {reportData && (
            <div className="table-responsive mt-4" ref={receiptRef}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-white"
                    >
                      {/* {`Monthly Report For $ ${selectedYear}`} */}
                      Monthly Report For {getMonthName(selectedMonth)}{" "}
                      {selectedYear}
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Total Collection
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {[
                    {
                      label: "Online Registration",
                      value: reportData.online_registration || 0,
                    },
                    {
                      label: "Counter Registration ECS",
                      value: reportData.counter_registration_ecs || 0,
                    },
                    // {
                    //   label: "Cash Receipt",
                    //   value: reportData.cash_receipt || 0,
                    // },
                    {
                      label: "Cheque Amount",
                      value: reportData?.total_cheque_amount || 0,
                    },
                    {
                      label: "Online Amount",
                      value: reportData?.total_online_amount || 0,
                    },
                    {
                      label: "Cash Amount",
                      value: reportData?.total_cash_amount || 0,
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-medium">{row.label}</td>
                      <td colSpan="2" className="text-center">
                        {formatIndianCurrency(row.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Monthly Report For April 2025
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Mandate Report
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td className="fw-medium">Mandates Sent</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.cash_receipt ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">Newly Active Mandates</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.online_amount ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">Rejected Mandates</td>
                    <td colSpan="2" className="text-center">
                      {reportData?.users?.find(
                        (item) => item.status === "rejected",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                </tbody>

                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Online Donation Report
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Online Donation Report
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-medium">ONE TIME</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.total_donations[0]?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TOTAL RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {/* {reportData?.donations
                        .reduce(
                          (acc, itr) => acc + Number(itr.total_amount || 0),
                          0
                        )
                        .toLocaleString() ?? "0"
                        
                        } */}
                      {formatIndianCurrency(
                        (reportData?.donations || []).reduce(
                          (acc, itr) => acc + Number(itr.total_amount || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">YES BANK RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "YES BANK",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TECH PROCESS RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "TECH PROCESS",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TP3489 RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "TP3489",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">RAZORPAY RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "RAZORPAY",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">PHONEPAY RECURRING</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "PHONE PAY",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SBI SI</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "SBI SI",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SBI ACH</td>
                    <td colSpan="2" className="text-center">
                      {formatIndianCurrency(
                        reportData?.donations?.find(
                          (item) => item.processing_agency === "SBI ACH",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                  </tr>
                </tbody>

                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Online Donation Report
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Overall Rejected Recurring Transactions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-medium">Processing Agency</td>
                    <td className="fw-medium">Amount</td>
                    <td className="fw-medium">Count</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">YES BANK</td>
                    <td>
                      {formatIndianCurrency(
                        reportData?.reject_donations?.find(
                          (item) => item.processing_agency === "YES BANK",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                    <td>
                      {reportData?.reject_donations?.find(
                        (item) => item.processing_agency === "YES BANK",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">TECH PROCESS</td>
                    <td>
                      {formatIndianCurrency(
                        reportData?.reject_donations?.find(
                          (item) => item.processing_agency === "TECH PROCESS",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                    <td>
                      {reportData?.reject_donations?.find(
                        (item) => item.processing_agency === "TECH PROCESS",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-medium">RAZORPAY</td>
                    <td>
                      {formatIndianCurrency(
                        reportData?.reject_donations?.find(
                          (item) => item.processing_agency === "RAZORPAY",
                        )?.total_amount ?? "0",
                      )}
                    </td>
                    <td>
                      {reportData?.reject_donations?.find(
                        (item) => item.processing_agency === "RAZORPAY",
                      )?.total_count ?? "0"}
                    </td>
                  </tr>
                </tbody>
                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Active Patrons Reports
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Total Active Patrons Summary
                    </th>
                  </tr>
                  <tr>
                    <th className="fw-medium">Category</th>
                    <th className="fw-medium text-center">Active</th>
                    <th className="fw-medium text-center">On-Hold / Closed</th>
                  </tr>
                </thead>

                <tbody>
                  {ALL_LEVELS.map((level, index) => {
                    const data = levelDataMap[level] || {};

                    return (
                      <tr key={index}>
                        <td className="fw-medium">{level}</td>
                        <td className="text-center">{data.active || 0}</td>
                        <td className="text-center">
                          {data.onHold || 0} / {data.closed || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                <thead>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fs-5 fw-bold bg-primary text-primary"
                    >
                      Active Patrons Reports
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan="3"
                      className="text-center fw-semibold bg-info text-dark"
                    >
                      Overall Information
                    </th>
                  </tr>
                </thead>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
